import axios from 'axios';

/**
 * The single axios instance for the console.
 *
 * Deliberately NOT the pattern from the garage app's `client/src/utils/api.js`,
 * which the spec calls the weakest thing in the codebase: there, every call
 * swallows its error and returns `null` or `{success:false}`, so a failed
 * request is indistinguishable from an empty result and react-query can never
 * see a failure. Here errors are normalised and RE-THROWN, so loading, empty
 * and broken are three distinct states the UI can render differently.
 */

const TOKEN_KEY = 'jobcard.admin.token';

// Written synchronously at module load, before React renders, so a request
// fired during the very first render already carries the token.
let authToken = null;
try {
  authToken = window.localStorage.getItem(TOKEN_KEY);
} catch {
  // Private browsing or a blocked storage partition — the console still works
  // for the length of the tab, it just will not survive a reload.
  authToken = null;
}

const api = axios.create({
  baseURL: import.meta.env.VITE_ADMIN_API_URL || '',
  timeout: 45000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  if (authToken) {
    // `x-admin-token`, not the garage app's `x-auth-token`: the two schemes are
    // separate on purpose so neither header can ever satisfy the other API.
    config.headers['x-admin-token'] = authToken;
  }
  return config;
});

/** Subscribers notified when the session ends (expiry, revocation, demotion). */
const sessionListeners = new Set();
export const onSessionEnded = (fn) => {
  sessionListeners.add(fn);
  return () => sessionListeners.delete(fn);
};

/** Normalised error the whole UI can rely on. */
export class ApiError extends Error {
  constructor(message, { status, details, code } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.code = code;
  }

  /** Field errors from express-validator, ready to drop onto form inputs. */
  get fieldErrors() {
    if (!Array.isArray(this.details)) return {};
    return this.details.reduce((acc, d) => {
      if (d.field) acc[d.field] = d.message;
      return acc;
    }, {});
  }
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // No response at all: the API is down, DNS failed, or CORS rejected us.
    if (!error.response) {
      const message = error.code === 'ECONNABORTED'
        ? 'The request timed out. The report may be too large, or the API is overloaded.'
        : 'Cannot reach the admin API. Check that it is running and that this origin is in ADMIN_CORS_ORIGINS.';
      return Promise.reject(new ApiError(message, { code: error.code }));
    }

    const { status, data } = error.response;
    const message = data?.error?.message || data?.msg || `Request failed (${status})`;

    // 401 means the token is gone, expired, or was revoked by a logout
    // elsewhere. 403 does NOT clear the session — it usually means this
    // particular action is not allowed, and dumping the operator back to the
    // login screen would lose their place for no reason.
    if (status === 401) {
      setAuthToken(null);
      sessionListeners.forEach((fn) => fn(message));
    }

    return Promise.reject(new ApiError(message, {
      status,
      details: data?.error?.details,
    }));
  },
);

export function setAuthToken(token) {
  authToken = token || null;
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Non-fatal; see the note at the top.
  }
}

export const getAuthToken = () => authToken;

/** Unwraps `{ success, data }` so callers deal in the payload, not the envelope. */
export async function request(config) {
  const response = await api.request(config);
  const body = response.data;
  if (body && typeof body === 'object' && 'success' in body) {
    return body.success
      ? body
      : Promise.reject(new ApiError(body.error?.message || 'Request failed', {
        status: response.status,
        details: body.error?.details,
      }));
  }
  return body;
}

export const get = (url, params, config) => request({ method: 'get', url, params, ...config });
export const post = (url, data, config) => request({ method: 'post', url, data, ...config });
export const put = (url, data, config) => request({ method: 'put', url, data, ...config });
export const patch = (url, data, config) => request({ method: 'patch', url, data, ...config });
export const del = (url, config) => request({ method: 'delete', url, ...config });

/**
 * Multipart upload — the Landing Page editor's image picker is the only
 * caller. Axios sets its own `multipart/form-data` boundary header when the
 * body is a `FormData` instance, so the instance's default `application/json`
 * header has to be explicitly cleared rather than left to be "overridden";
 * some environments keep the manually-set header instead of replacing it.
 */
export const uploadFile = (url, formData, config) => request({
  method: 'post',
  url,
  data: formData,
  headers: { 'Content-Type': undefined },
  ...config,
});

/**
 * Triggers a browser download for an endpoint that returns a file.
 *
 * The API sets Content-Disposition, but a plain `<a href>` cannot send the
 * `x-admin-token` header, so the file is fetched as a blob through the same
 * authenticated instance and handed to the browser as an object URL.
 */
export async function download(url, params, fallbackFilename) {
  const response = await api.request({ method: 'get', url, params, responseType: 'blob' });

  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackFilename;

  const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke on the next tick — revoking synchronously can cancel the download
  // in some browsers before it has started reading the blob.
  setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);

  return filename;
}

export default api;
